import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  Camera,
  Loader2,
  Copy,
  Download,
  Trash2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  ScanLine,
  FileSearch,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import Tesseract from 'tesseract.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Processing stages for animation
const STAGES = [
  { id: 'loading', label: 'Loading image...' },
  { id: 'preprocessing', label: 'Preprocessing...' },
  { id: 'recognizing', label: 'Recognizing text...' },
  { id: 'complete', label: 'Complete!' }
];

export const DocumentOCR = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/bmp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (PNG, JPEG, WebP, BMP, GIF)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setExtractedText('');
    setError(null);
    setProgress(0);
    setZoom(1);
    setRotation(0);
  }, []);

  // Process image with Tesseract.js
  const processImage = useCallback(async () => {
    if (!selectedFile && !previewUrl) {
      toast.error('Please select an image first');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError(null);
    setCurrentStage('loading');

    try {
      const result = await Tesseract.recognize(
        previewUrl || selectedFile,
        'eng', // Language
        {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setCurrentStage('recognizing');
              setProgress(Math.round(m.progress * 100));
            } else if (m.status === 'loading tesseract core') {
              setCurrentStage('loading');
              setProgress(10);
            } else if (m.status === 'initializing tesseract') {
              setCurrentStage('preprocessing');
              setProgress(30);
            } else if (m.status === 'loading language traineddata') {
              setCurrentStage('preprocessing');
              setProgress(50);
            }
          }
        }
      );

      setCurrentStage('complete');
      setProgress(100);
      setExtractedText(result.data.text);

      // Save to history
      const docRecord = {
        id: `doc_${Date.now()}`,
        filename: selectedFile?.name || 'Camera capture',
        text: result.data.text,
        confidence: result.data.confidence,
        timestamp: new Date().toISOString(),
        wordCount: result.data.words?.length || 0
      };
      setDocuments(prev => [docRecord, ...prev].slice(0, 20));

      toast.success(`Text extracted successfully! Confidence: ${Math.round(result.data.confidence)}%`);
    } catch (err) {
      console.error('OCR Error:', err);
      setError(err.message || 'Failed to process image');
      toast.error('Failed to extract text from image');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedFile, previewUrl]);

  // Copy text to clipboard
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(extractedText);
    toast.success('Text copied to clipboard');
  }, [extractedText]);

  // Download as text file
  const downloadText = useCallback(() => {
    const blob = new Blob([extractedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr_${selectedFile?.name?.split('.')[0] || 'document'}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Text file downloaded');
  }, [extractedText, selectedFile]);

  // Clear current selection
  const clearSelection = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setExtractedText('');
    setError(null);
    setProgress(0);
    setZoom(1);
    setRotation(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, []);

  // Load from history
  const loadFromHistory = useCallback((doc) => {
    setExtractedText(doc.text);
    setShowHistory(false);
    toast.success('Document loaded from history');
  }, []);

  return (
    <div className="space-y-6" data-testid="document-ocr">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <FileSearch className="w-6 h-6 mr-2 text-neon-cyan" />
            Document OCR
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Extract text from images using AI-powered optical character recognition
          </p>
        </div>
        <button
          onClick={() => setShowHistory(true)}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white flex items-center space-x-2"
        >
          <FileText className="w-4 h-4" />
          <span>History ({documents.length})</span>
        </button>
      </div>

      {/* Upload Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Image Input Section */}
        <div className="space-y-4">
          {/* Upload/Camera buttons */}
          <div className="flex space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="ocr-file-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="ocr-camera-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 py-3 bg-neon-cyan/20 hover:bg-neon-cyan/30 border border-neon-cyan/50 rounded-xl text-neon-cyan flex items-center justify-center space-x-2 transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span>Upload Image</span>
            </button>
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="flex-1 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-xl text-purple-400 flex items-center justify-center space-x-2 transition-colors"
            >
              <Camera className="w-5 h-5" />
              <span>Take Photo</span>
            </button>
          </div>

          {/* Preview Area */}
          <div className="glass-card p-4 min-h-[400px] flex flex-col">
            {previewUrl ? (
              <>
                {/* Image controls */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-400"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-slate-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
                    <button
                      onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-400"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setRotation(r => (r + 90) % 360)}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-400"
                      title="Rotate"
                    >
                      <RotateCw className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={clearSelection}
                    className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400"
                    title="Clear"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Image preview */}
                <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-900/50 rounded-lg">
                  <img
                    src={previewUrl}
                    alt="Document preview"
                    className="max-w-full object-contain transition-transform"
                    style={{
                      transform: `scale(${zoom}) rotate(${rotation}deg)`
                    }}
                  />
                </div>

                {/* Process button */}
                <button
                  onClick={processImage}
                  disabled={isProcessing}
                  className="mt-4 w-full py-3 bg-neon-cyan text-slate-900 rounded-xl font-medium hover:bg-neon-cyan/80 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ocr-process-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Processing... {progress}%</span>
                    </>
                  ) : (
                    <>
                      <ScanLine className="w-5 h-5" />
                      <span>Extract Text</span>
                    </>
                  )}
                </button>
              </>
            ) : (
              <div 
                className="flex-1 flex flex-col items-center justify-center text-slate-500 cursor-pointer border-2 border-dashed border-white/10 rounded-xl hover:border-neon-cyan/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg font-medium">Drop image here</p>
                <p className="text-sm mt-1">or click to browse</p>
                <p className="text-xs mt-4">Supports: PNG, JPEG, WebP, BMP, GIF</p>
              </div>
            )}
          </div>

          {/* Progress indicator */}
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white">
                  {STAGES.find(s => s.id === currentStage)?.label || 'Processing...'}
                </span>
                <span className="text-sm text-neon-cyan">{progress}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-neon-cyan"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <div className="flex justify-between mt-3">
                {STAGES.map((stage, idx) => (
                  <div
                    key={stage.id}
                    className={`flex items-center space-x-1 text-xs ${
                      STAGES.findIndex(s => s.id === currentStage) >= idx
                        ? 'text-neon-cyan'
                        : 'text-slate-500'
                    }`}
                  >
                    {STAGES.findIndex(s => s.id === currentStage) > idx ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : STAGES.findIndex(s => s.id === currentStage) === idx ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-current" />
                    )}
                    <span className="hidden sm:inline">{stage.label.replace('...', '')}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Extracted Text Section */}
        <div className="space-y-4">
          <div className="glass-card p-4 h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-white flex items-center">
                <FileText className="w-4 h-4 mr-2 text-neon-cyan" />
                Extracted Text
              </h3>
              {extractedText && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={copyToClipboard}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-400 hover:text-white"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={downloadText}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-400 hover:text-white"
                    title="Download as text file"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {error ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <p className="text-red-400">{error}</p>
                  <button
                    onClick={clearSelection}
                    className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : extractedText ? (
              <div className="flex-1 overflow-auto">
                <textarea
                  value={extractedText}
                  onChange={(e) => setExtractedText(e.target.value)}
                  className="w-full h-full min-h-[400px] bg-slate-900/50 rounded-lg p-4 text-white text-sm font-mono resize-none border border-white/10 focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="Extracted text will appear here..."
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <ScanLine className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Upload an image and click "Extract Text"</p>
                  <p className="text-sm mt-1">Supported languages: English</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="glass-card p-4">
        <h3 className="font-medium text-white mb-3">Tips for Better Results</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-400">
          <div className="flex items-start space-x-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <span>Use clear, high-resolution images with good lighting</span>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <span>Ensure text is horizontal and not skewed</span>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <span>Avoid shadows and glare on documents</span>
          </div>
        </div>
      </div>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h3 className="text-lg font-bold text-white">OCR History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {documents.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No documents processed yet</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
                        onClick={() => loadFromHistory(doc)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-white">{doc.filename}</span>
                          <span className="text-xs text-slate-400">
                            {new Date(doc.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 line-clamp-2">{doc.text}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                          <span>{doc.wordCount} words</span>
                          <span>Confidence: {Math.round(doc.confidence)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentOCR;
